/**
 * Books: a long text rendered as one video per chapter (engine app/routers/books_api.py).
 *
 * A book's chapters are ordinary videos once started, and they come back in
 * GET /videos as well, each carrying a `chapter` ref, so everything that already
 * acts on a video (live progress, confirm, cancel, retry, delete) acts on a
 * chapter unchanged.
 *
 * Chapters start one at a time: approving one starts the next. When the next one
 * could not start then (credits, say), the book is `paused` and `continueBook`
 * starts it.
 */
import { getJson, postJson, resolveApiUrl } from '@/shared/api/apiClient';
import { normalizeStatus, type TaskApiItem } from '@/shared/api/backendContract';
import type { BookChapterRef, LibraryItem, ProcessingStatus } from '@/shared/types';
import { applyLibraryDefaults, taskToApiItem } from '@/features/library/services/libraryApi';

const BOOKS_PATH = '/api/v1/books';

type BookApiItem = {
  book_id: string;
  title?: string | null;
  created_at: string;
  video_count: number;
  completed_count: number;
  not_started_count: number;
  status: string;
  can_continue: boolean;
  thumbnail_url?: string | null;
};

type UpcomingApiItem = {
  chapter_index: number;
  part_index: number;
  chapter_title?: string | null;
  word_count: number;
};

type BookDetailApiItem = BookApiItem & { chapters: TaskApiItem[]; upcoming: UpcomingApiItem[] };

/** A video status, or `paused`: nothing running and the next chapter waiting to be started. */
export type BookStatus = ProcessingStatus | 'paused';

/** A chapter not started yet. It has no video until the one before it is approved. */
export type UpcomingChapter = BookChapterRef & { wordCount: number };

export type Book = {
  id: string;
  title: string;
  createdAt: string;
  /** Videos, not chapters: a chapter too long for one video is several parts. */
  videoCount: number;
  completedCount: number;
  notStartedCount: number;
  /** The whole book's state, rolled up server-side from its videos. */
  status: BookStatus;
  /** Whether `continueBook` would start the next chapter now. */
  canContinue: boolean;
  thumbnailUrl?: string;
};

export type BookDetail = Book & {
  /** The started chapters, in reading order: chapter, then part. */
  chapters: LibraryItem[];
  /** The ones not started yet, which always come after them. */
  upcoming: UpcomingChapter[];
};

const UNTITLED_BOOK = 'Untitled book';

function toBook(item: BookApiItem): Book {
  return {
    id: item.book_id,
    title: item.title?.trim() || UNTITLED_BOOK,
    createdAt: item.created_at,
    videoCount: item.video_count,
    completedCount: item.completed_count,
    notStartedCount: item.not_started_count,
    status: item.status === 'paused' ? 'paused' : normalizeStatus(item.status),
    canContinue: item.can_continue,
    thumbnailUrl: item.thumbnail_url ? resolveApiUrl(item.thumbnail_url) : undefined,
  };
}

export async function listBooks(): Promise<Book[]> {
  const items = await getJson<BookApiItem[]>(BOOKS_PATH);
  return items.map(toBook);
}

export async function getBook(bookId: string): Promise<BookDetail> {
  const item = await getJson<BookDetailApiItem>(`${BOOKS_PATH}/${encodeURIComponent(bookId)}`);
  return {
    ...toBook(item),
    chapters: item.chapters.map((chapter) => applyLibraryDefaults(taskToApiItem(chapter))),
    upcoming: item.upcoming.map((chapter) => ({
      bookId: item.book_id,
      chapterIndex: chapter.chapter_index,
      partIndex: chapter.part_index,
      chapterTitle: chapter.chapter_title ?? undefined,
      wordCount: chapter.word_count,
    })),
  };
}

/** Start the next chapter of a paused book. Returns its new video's id. */
export async function continueBook(bookId: string): Promise<string> {
  const response = await postJson<{ job_id: string }>(
    `${BOOKS_PATH}/${encodeURIComponent(bookId)}/continue`,
  );
  return response.job_id;
}
